/**
 * The box opener's sound events and what a tenant or shopper may set on them. The keys and
 * fallbacks mirror the engine's catalog (`OPENER_SOUND_EVENTS` in `@loot-os/react`, which cannot
 * import this package), and the labels and descriptions are the ones the dashboard shows.
 *
 * A tenant's setting per event is a replacement file (an `https` URL, with an optional trim in
 * dB), or `{ off: true }`; a missing key inherits (a battle event, its opener twin, then the
 * platform's sound). A shopper only mutes and sets a volume.
 */
import { z } from "zod";

/** Every sound event, in the order the dashboard lists them. */
export const SOUND_EVENT_KEYS = [
  "opener.spinner.tick",
  "opener.reveal.bad",
  "opener.reveal.rare",
  "opener.reveal.mega",
  "battle.spinner.tick",
  "battle.reveal.bad",
  "battle.reveal.rare",
  "battle.reveal.mega",
] as const;
export type SoundEventKey = (typeof SOUND_EVENT_KEYS)[number];
export const soundEventKeySchema = z.enum(SOUND_EVENT_KEYS);

/** One event as the dashboard lists it; `fallback` is the event it plays when unset. */
export interface SoundEventInfo {
  key: SoundEventKey;
  label: string;
  description: string;
  group: "Box opening" | "Battles";
  fallback?: SoundEventKey;
}

/** Every event with its words; the group heading tells the two sets of labels apart. */
export const SOUND_EVENTS: readonly SoundEventInfo[] = [
  {
    key: "opener.spinner.tick",
    label: "Reel tick",
    description: "A reel item passes the pointer",
    group: "Box opening",
  },
  {
    key: "opener.reveal.bad",
    label: "Low pull",
    description: "A low-value pull settles",
    group: "Box opening",
  },
  {
    key: "opener.reveal.rare",
    label: "Good pull",
    description: "A good pull settles",
    group: "Box opening",
  },
  {
    key: "opener.reveal.mega",
    label: "Big win",
    description: "A top-tier pull settles",
    group: "Box opening",
  },
  {
    key: "battle.spinner.tick",
    label: "Reel tick",
    description: "A battle reel item passes the pointer",
    group: "Battles",
    fallback: "opener.spinner.tick",
  },
  {
    key: "battle.reveal.bad",
    label: "Low pull",
    description: "A low-value battle pull settles",
    group: "Battles",
    fallback: "opener.reveal.bad",
  },
  {
    key: "battle.reveal.rare",
    label: "Good pull",
    description: "A good battle pull settles",
    group: "Battles",
    fallback: "opener.reveal.rare",
  },
  {
    key: "battle.reveal.mega",
    label: "Big win",
    description: "A top-tier battle pull settles",
    group: "Battles",
    fallback: "opener.reveal.mega",
  },
];

/** The trim a tenant may put on a replacement, in dB; a missing `gainDb` means 0. */
export const SOUND_GAIN_DB_MIN = -24;
export const SOUND_GAIN_DB_MAX = 6;

/** `https`, or `http` on this machine: local dev serves uploads from http://localhost. */
function isServableSoundUrl(url: string): boolean {
  const { protocol, hostname } = new URL(url);
  if (protocol === "https:") return true;
  return protocol === "http:" && (hostname === "localhost" || hostname === "127.0.0.1");
}

/**
 * One event's setting: a replacement file or off. The URL is `https` (outside local dev), since
 * the opener fetches it from the shopper's browser.
 */
export const soundSettingSchema = z.union([
  z.strictObject({
    url: z
      .url({ protocol: /^https?$/ })
      .max(2048)
      .refine(isServableSoundUrl, "the sound URL must be https"),
    gainDb: z.number().min(SOUND_GAIN_DB_MIN).max(SOUND_GAIN_DB_MAX).optional(),
  }),
  z.strictObject({ off: z.literal(true) }),
]);
export type SoundSetting = z.infer<typeof soundSettingSchema>;

/** The stored and wire shape: each customized event's setting, no others (`{}` = inherit all). */
export const soundSettingsSchema = z.partialRecord(soundEventKeySchema, soundSettingSchema);
export type SoundSettings = z.infer<typeof soundSettingsSchema>;

/**
 * The sounds a storefront plays, resolved for the browser: each event with a replacement file and
 * its trim in dB (0 when unset), or `{ off: true }` for one turned off, which the opener plays as
 * silence rather than falling back. An event with no setting anywhere is absent.
 */
export const storefrontSoundsSchema = z.partialRecord(
  soundEventKeySchema,
  z.union([z.object({ url: z.string(), gainDb: z.number() }), z.object({ off: z.literal(true) })]),
);
export type StorefrontSounds = z.infer<typeof storefrontSoundsSchema>;

/** An event's effective setting, where it came from and, for a fallback, the event it came by. */
export type ResolvedSound = {
  setting: SoundSetting;
  source: "environment" | "platform";
  via?: SoundEventKey;
};

/**
 * Every event with a setting in force. Per event the first hit wins: the environment's own entry,
 * the environment's entry for the fallback event, the platform's entry, the platform's entry for
 * the fallback event. An event with no hit is absent, and silent: there are no built-in sounds.
 */
export function resolveStorefrontSounds(
  platform: SoundSettings,
  overrides: SoundSettings,
): Partial<Record<SoundEventKey, ResolvedSound>> {
  const resolved: Partial<Record<SoundEventKey, ResolvedSound>> = {};
  for (const { key, fallback } of SOUND_EVENTS) {
    const candidates: Array<[SoundSettings, ResolvedSound["source"], SoundEventKey | undefined]> = [
      [overrides, "environment", undefined],
      [overrides, "environment", fallback],
      [platform, "platform", undefined],
      [platform, "platform", fallback],
    ];
    for (const [settings, source, via] of candidates) {
      const setting = settings[via ?? key];
      if (setting === undefined) continue;
      resolved[key] = via ? { setting, source, via } : { setting, source };
      break;
    }
  }
  return resolved;
}

/** Uploads are MP3 alone. */
export const SOUND_UPLOAD_CONTENT_TYPES = ["audio/mpeg"] as const;
export type SoundContentType = (typeof SOUND_UPLOAD_CONTENT_TYPES)[number];
/** The file picker's `accept` for a sound upload. */
export const SOUND_FILE_ACCEPT = "audio/mpeg,.mp3";

export function isSoundContentType(value: string): value is SoundContentType {
  return (SOUND_UPLOAD_CONTENT_TYPES as readonly string[]).includes(value);
}

/** The shopper's volume slider (0-100) when they have not moved it. */
export const SHOPPER_SOUND_VOLUME_DEFAULT = 50;

/** The engine gain for a shopper's volume: a square curve, so the slider's middle is a quarter. */
export function shopperVolumeGain(volume: number): number {
  const clamped = Math.min(100, Math.max(0, volume));
  return (clamped / 100) ** 2;
}

/**
 * Settings read from storage, with the keys this code no longer knows dropped so a retired event
 * never refuses the rest. A bad value under a known key, or a non-object, throws zod's error.
 */
export function sanitizeSoundSettings(value: unknown): SoundSettings {
  const known: readonly string[] = SOUND_EVENT_KEYS;
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    return soundSettingsSchema.parse(value);
  }
  return soundSettingsSchema.parse(
    Object.fromEntries(Object.entries(value).filter(([key]) => known.includes(key))),
  );
}
